import { useEffect } from 'react';

const MODAL_ROOT_SELECTOR = '.MuiDialog-root, .MuiDrawer-root, .MuiModal-root';

export function useModalFocusRestoration() {
  useEffect(() => {
    const openModals = [];
    let lastFocusedElement = null;
    const handleFocusIn = (event) => {
      if (!event.target.closest?.(MODAL_ROOT_SELECTOR)) {
        lastFocusedElement = event.target;
      }
    };

    const addModal = (modal) => {
      if (
        modal.matches(MODAL_ROOT_SELECTOR) &&
        modal.getAttribute('aria-hidden') !== 'true' &&
        !openModals.some((entry) => entry.modal === modal)
      ) {
        const { activeElement } = document;
        const opener = modal.contains(activeElement) ? lastFocusedElement : activeElement;
        openModals.push({ modal, opener });
      }
    };

    const closeModal = (modal) => {
      const index = openModals.findIndex((entry) => entry.modal === modal);
      if (index === -1) return;

      const [{ opener }] = openModals.splice(index, 1);
      if (index !== openModals.length) return;

      requestAnimationFrame(() => {
        if (
          opener instanceof HTMLElement &&
          opener.isConnected &&
          !modal.contains(opener) &&
          !opener.closest('[aria-hidden="true"]')
        ) {
          opener.focus({ preventScroll: true });
        }
      });
    };

    const visitModalRoots = (node, callback) => {
      if (!(node instanceof Element)) return;
      if (node.matches(MODAL_ROOT_SELECTOR)) callback(node);
      node.querySelectorAll(MODAL_ROOT_SELECTOR).forEach(callback);
    };

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => visitModalRoots(node, addModal));
          mutation.removedNodes.forEach((node) => visitModalRoots(node, closeModal));
        } else if (mutation.attributeName === 'aria-hidden') {
          if (mutation.target.getAttribute('aria-hidden') === 'true') {
            closeModal(mutation.target);
          } else {
            addModal(mutation.target);
          }
        }
      });
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['aria-hidden'],
      childList: true,
      subtree: false,
    });
    document.addEventListener('focusin', handleFocusIn);

    return () => {
      observer.disconnect();
      document.removeEventListener('focusin', handleFocusIn);
    };
  }, []);
}
